// src/components/Auth.js
import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  signInWithPopup,
  GoogleAuthProvider,
  OAuthProvider
} from 'firebase/auth';
import { auth } from '../firebase';
import { setDoc, doc, serverTimestamp, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { Loader2, Lock, Mail, CheckCircle2, Sparkles } from 'lucide-react';
import Logo from './Logo';
import Footer from './Footer';
import SEO from './SEO';
import { requestPasswordReset, sendWelcomeEmail } from '../utils/email';
import { sendFirebaseVerificationEmail } from '../utils/emailVerification';
import { encrypt } from '../utils/encryption';

function Auth() {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const navigate = useNavigate();



  const getFriendlyErrorMessage = (errorCode, errorMessage) => {
    switch (errorCode) {
      case 'auth/invalid-credential':
      case 'auth/wrong-password':
      case 'auth/user-not-found':
        return {
          message: "The email or password you entered is incorrect.",
          showSignUpLink: true,
          showForgotPassword: true
        };
      case 'auth/email-already-in-use':
        return {
          message: "An account with this email already exists. Try logging in instead.",
          showSignUpLink: false,
          showForgotPassword: true
        };
      case 'auth/weak-password':
        return {
          message: "Password should be at least 6 characters long.",
          showSignUpLink: false,
          showForgotPassword: false
        };
      case 'auth/invalid-email':
        return {
          message: "Please enter a valid email address.",
          showSignUpLink: false,
          showForgotPassword: false
        };
      case 'auth/too-many-requests':
        return {
          message: "Too many failed login attempts. Please try again later or reset your password.",
          showSignUpLink: false,
          showForgotPassword: true
        };
      case 'auth/unauthorized-domain': {
        const host = typeof window !== 'undefined' ? window.location.hostname : '';
        const origin = typeof window !== 'undefined' ? window.location.origin : '';
        return {
          message:
            `Firebase is blocking sign-in from this address. In Firebase Console: Authentication → Settings → Authorized domains → Add domain, and enter "${host}" (hostname only; no http or port). ` +
            (origin
              ? `If you use Google sign-in, also open Google Cloud Console → APIs & Services → Credentials → your OAuth 2.0 Web client → Authorized JavaScript origins and add "${origin}". `
              : '') +
            `Alternatively, use http://localhost:3000 or http://localhost:3001 if those are already authorized.`,
          showSignUpLink: false,
          showForgotPassword: false
        };
      }
      default: {
        const defaultError = {
          message: errorMessage || "An error occurred. Please try again.",
          showSignUpLink: false,
          showForgotPassword: false
        };
        return defaultError;
      }
    }
  };

  const handleAuth = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      if (isLogin) {
        await signInWithEmailAndPassword(auth, email, password);
        navigate('/');
        return;
      }

      if (password !== confirmPassword) {
        setError({
          message: "Passwords do not match. Please try again.",
          showSignUpLink: false,
          showForgotPassword: false
        });
        return;
      }

      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      // Encrypt sensitive data before saving (matches UserProfile.js pattern)
      const encryptionKey = process.env.REACT_APP_ENCRYPTION_KEY;
      await setDoc(doc(db, 'users', user.uid), {
        firstName: encrypt(firstName, encryptionKey),
        lastName: encrypt(lastName, encryptionKey),
        email: encrypt(email, encryptionKey),
        createdAt: serverTimestamp(),
      });

      // US-11: Verification link via API (Resend) — required for complimentary AI pass
      try {
        await sendFirebaseVerificationEmail();
      } catch (verifyErr) {
        console.warn('[Auth] Email verification send:', verifyErr?.message || verifyErr);
      }

      // Send welcome email after account creation
      try {
        console.log('[Auth] Sending welcome email to:', user.email);
        await sendWelcomeEmail(user.email, firstName || 'there');
        console.log('[Auth] Welcome email sent successfully');
      } catch (emailError) {
        console.error('[Auth] Failed to send welcome email:', {
          error: emailError.message,
          stack: emailError.stack,
          email: user.email
        });
      }

      navigate('/', { state: { emailVerificationSent: true } });
    } catch (err) {
      // Handle Firebase errors with friendly messages
      // Firebase errors can have different structures
      let errorCode = '';
      let errorMessage = '';
      
      // Firebase errors have a 'code' property (e.g., 'auth/invalid-credential')
      // and a 'message' property (e.g., 'Firebase: Error (auth/invalid-credential)')
      // Always check code first - Firebase Auth errors always have this
      if (err?.code) {
        errorCode = err.code;
        errorMessage = err.message || '';
      } else if (err?.error?.code) {
        errorCode = err.error.code;
        errorMessage = err.error.message || '';
      } else if (typeof err === 'string') {
        errorMessage = err;
      } else {
        errorMessage = err?.message || err?.toString() || 'An error occurred';
      }
      
      // ALWAYS try to extract code from message if code wasn't found
      // Firebase error messages are like "Firebase: Error (auth/invalid-credential)"
      // Even if err.code exists but is empty, check message as fallback
      if (!errorCode && errorMessage) {
        // Extract code from message like "Firebase: Error (auth/invalid-credential)"
        const codeMatch = errorMessage.match(/\(auth\/[^)]+\)/);
        if (codeMatch) {
          errorCode = codeMatch[0].slice(1, -1); // Remove parentheses: "(auth/invalid-credential)" -> "auth/invalid-credential"
        }
      }
      
      // Additional fallback pattern matching - check message even if we have a code
      // This handles cases where err.code might be missing but message has the info
      if (!errorCode && errorMessage) {
        if (errorMessage.includes('invalid-credential') || errorMessage.includes('wrong-password') || errorMessage.includes('user-not-found')) {
          errorCode = 'auth/invalid-credential';
        } else if (errorMessage.includes('email-already-in-use')) {
          errorCode = 'auth/email-already-in-use';
        } else if (errorMessage.includes('weak-password')) {
          errorCode = 'auth/weak-password';
        } else if (errorMessage.includes('invalid-email')) {
          errorCode = 'auth/invalid-email';
        }
      }
      
      // Final fallback: if we still don't have a code but have a message that looks like Firebase error
      // Try to extract from the toString() representation
      if (!errorCode && errorMessage && errorMessage.includes('Firebase')) {
        const codeMatch = errorMessage.match(/\(auth\/[^)]+\)/);
        if (codeMatch) {
          errorCode = codeMatch[0].slice(1, -1);
        }
      }
      
      const friendlyError = getFriendlyErrorMessage(errorCode, errorMessage);
      setError(friendlyError);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!email) {
      setError({
        message: "Please enter your email address first.",
        showSignUpLink: false,
        showForgotPassword: false
      });
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      // Request password reset via Resend
      await requestPasswordReset(email);
      setSuccess("Password reset email sent! Check your inbox and spam folder. You should receive an email with a reset link shortly. The link will expire in 1 hour.");
    } catch (err) {
      // Handle errors
      if (err.message.includes('not found') || err.message.includes('No account')) {
        setError({
          message: "No account found with this email address. Please check your email or sign up for a new account.",
          showSignUpLink: true,
          showForgotPassword: false
        });
      } else {
        setError({
          message: err.message || "Failed to send password reset email. Please try again or contact support.",
          showSignUpLink: false,
          showForgotPassword: false
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setLoading(true);
    setError(null);

    try {
      // Use popup for both mobile and desktop - simple and works
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      const user = result.user;

      // Check if user document exists, if not create it
      const userDocRef = doc(db, 'users', user.uid);
      const userDoc = await getDoc(userDocRef);

      if (!userDoc.exists()) {
        // Extract name from Google account
        const displayName = user.displayName || '';
        const nameParts = displayName.split(' ');
        const firstName = nameParts[0] || '';
        const lastName = nameParts.slice(1).join(' ') || '';

        // Encrypt sensitive data before saving (matches UserProfile.js pattern)
        const encryptionKey = process.env.REACT_APP_ENCRYPTION_KEY;
        await setDoc(userDocRef, {
          firstName: encrypt(firstName, encryptionKey),
          lastName: encrypt(lastName, encryptionKey),
          email: encrypt(user.email, encryptionKey),
          avatarUrl: user.photoURL || '',
          createdAt: serverTimestamp(),
        });
      }

      navigate('/');
    } catch (err) {
      // Don't show error for popup-closed-by-user
      if (err.code === 'auth/popup-closed-by-user') {
        return;
      }
      setError(getFriendlyErrorMessage(err.code || '', err.message || ''));
    } finally {
      setLoading(false);
    }
  };

  const handleAppleSignIn = async () => {
    setLoading(true);
    setError(null);

    try {
      // Use OAuthProvider for Apple Sign In
      const provider = new OAuthProvider('apple.com');
      provider.addScope('email');
      provider.addScope('name');
      
      const result = await signInWithPopup(auth, provider);
      const user = result.user;

      // Check if user document exists, if not create it
      const userDocRef = doc(db, 'users', user.uid);
      const userDoc = await getDoc(userDocRef);

      if (!userDoc.exists()) {
        // Apple-specific handling:
        // - displayName may be null if user chose to hide it
        // - email may be a private relay email (e.g., user@privaterelay.appleid.com)
        // - photoURL is typically null for Apple accounts
        
        // Extract name from Apple account (may be null)
        const displayName = user.displayName || '';
        const nameParts = displayName.split(' ');
        const firstName = nameParts[0] || '';
        const lastName = nameParts.slice(1).join(' ') || '';

        // Use email (may be relay email, which is valid and should be treated like a regular email)
        const userEmail = user.email || '';

        // Encrypt sensitive data before saving (matches UserProfile.js pattern)
        const encryptionKey = process.env.REACT_APP_ENCRYPTION_KEY;
        await setDoc(userDocRef, {
          firstName: encrypt(firstName, encryptionKey),
          lastName: encrypt(lastName, encryptionKey),
          email: encrypt(userEmail, encryptionKey), // Relay email is valid
          avatarUrl: user.photoURL || '', // Typically null for Apple
          createdAt: serverTimestamp(),
        });
      }

      navigate('/');
    } catch (err) {
      // Log error for debugging with detailed information
      console.error('[Apple Sign In] Error Code:', err.code);
      console.error('[Apple Sign In] Error Message:', err.message);
      console.error('[Apple Sign In] Full Error:', err);
      console.error('[Apple Sign In] Error Stack:', err.stack);
      
      // Don't show error for popup-closed-by-user
      if (err.code === 'auth/popup-closed-by-user') {
        return;
      }
      
      // Handle Apple-specific errors
      if (err.code === 'auth/unauthorized-domain') {
        setError(getFriendlyErrorMessage(err.code, err.message || ''));
      } else if (err.code === 'auth/account-exists-with-different-credential') {
        setError('An account already exists with this email address. Please sign in using the original method.');
      } else if (err.code === 'auth/operation-not-allowed') {
        setError('Apple Sign In web configuration required. Since iOS works, please verify in Firebase Console: 1) Toggle "Enable" for Apple provider and click "Save", 2) Verify domain epiphoric.com in Apple Developer Console, 3) Add authorization callback URL to your web Services ID.');
      } else if (err.code === 'auth/configuration-not-found' || err.code === 'auth/invalid-credential') {
        setError('Apple Sign In web configuration incomplete. Please verify: 1) Domain verification in Apple Developer Console, 2) Services ID configured for web, 3) Authorization callback URL added to Apple Developer Console.');
      } else if (err.message?.includes('network') || err.message?.includes('fetch')) {
        setError('Network error. Please check your connection and try again.');
      } else if (err.message?.includes('domain') || err.message?.includes('verification')) {
        setError('Domain verification required. Please verify epiphoric.com in Apple Developer Console for web sign-in.');
      } else {
        // Show more detailed error message for debugging
        const errorMsg = err.code 
          ? `Apple Sign In failed: ${err.code}${err.message ? ' - ' + err.message : ''}` 
          : err.message || 'Failed to sign in with Apple';
        setError(errorMsg);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <SEO 
        title="Epiphoric - AI-Powered Idea Development"
        description="Transform rough ideas into actionable plans with AI. Capture, develop, and organize your creative breakthroughs with end-to-end encryption and intelligent assistance."
        keywords="AI idea development, idea management, AI assistant, creative tools, project planning, encrypted notes, idea tracker, brainstorming AI, autopilot ideas, guided AI"
        url="/auth"
      />
      <div className="flex flex-col min-h-screen relative">
        <div className="grid grid-cols-1 md:grid-cols-2 flex-1 min-h-0">
        {/* Left Side: The Vibe */}
      <div className="bg-gradient-to-br from-slate-900 to-indigo-950 flex flex-col justify-between items-start p-8 md:p-12 lg:p-16 relative">
        {/* Logo at top */}
        <div className="mb-8">
          <Logo size="large" />
        </div>
        
        {/* Main Content - Centered */}
        <div className="flex-1 flex flex-col justify-center w-full">
          {/* Headline */}
          <h1 className="text-white text-5xl font-bold mb-4 leading-tight">
            Ideas, Meet AI.
          </h1>
          
          {/* Subhead */}
          <p className="text-slate-300 text-lg mt-4 leading-relaxed max-w-lg">
            Transform rough concepts into structured, actionable plans. AI-powered development 
            turns your spark of inspiration into a complete roadmap — instantly.
          </p>
          
          {/* Feature Pills */}
          <div className="flex flex-wrap gap-3 mt-6">
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium bg-purple-500/20 text-purple-200 border border-purple-400/30">
              <Sparkles className="w-3.5 h-3.5" />
              AI Autopilot
            </span>
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium bg-indigo-500/20 text-indigo-200 border border-indigo-400/30">
              <Lock className="w-3.5 h-3.5" />
              End-to-End Encrypted
            </span>
          </div>
          
          {/* Learn More Link */}
          <Link
            to="/how-it-works"
            className="inline-flex items-center gap-2 text-indigo-300 hover:text-indigo-200 mt-6 transition-colors text-sm font-medium"
          >
            See how AI develops your ideas →
          </Link>
        </div>

        {/* Footer: Trust Seal */}
        <div className="absolute bottom-24 left-8 md:left-12 lg:left-16 flex items-center gap-2 text-indigo-200 text-sm">
          <Lock className="w-4 h-4" />
          <span>Your ideas are encrypted. AI enhancement uses your account entitlement (e.g. complimentary credit).</span>
        </div>
      </div>

      {/* Right Side: The Action */}
      <div className="bg-white dark:bg-slate-950 flex flex-col justify-center items-center p-8 md:p-12 overflow-y-auto min-h-0">
        <div className="w-full max-w-md pb-24 md:pb-0">
          <h2 className="text-3xl font-bold mb-6 text-gray-900 dark:text-white">
            {showForgotPassword ? 'Reset Password' : isLogin ? 'Login' : 'Sign Up'}
          </h2>
          <form onSubmit={showForgotPassword ? handleForgotPassword : handleAuth} className="w-full">
        {!isLogin && (
          <>
            <div className="mb-4">
              <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                First Name
              </label>
              <input
                id="firstName"
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                required={!isLogin}
              />
            </div>
            <div className="mb-4">
              <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Last Name
              </label>
              <input
                id="lastName"
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                required={!isLogin}
              />
            </div>
          </>
        )}
        <div className="mb-4">
          <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Email
          </label>
          <input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            required
          />
        </div>
        {!showForgotPassword && (
          <div className="mb-4">
            <div className="flex items-center justify-between mb-1">
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Password
              </label>
              {isLogin && (
                <button
                  type="button"
                  onClick={() => {
                    setShowForgotPassword(true);
                    setError(null);
                    setSuccess(null);
                  }}
                  className="text-sm text-primary hover:underline"
                >
                  Forgot password?
                </button>
              )}
            </div>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              required
            />
          </div>
        )}
        {isLogin && showForgotPassword && (
          <div className="mb-4 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
            <p className="text-sm text-gray-700 dark:text-gray-300 mb-3">
              Enter your email address and we'll send you a link to reset your password.
            </p>
            <button
              type="submit"
              disabled={loading || !email}
              className="w-full bg-primary hover:bg-primary/90 text-white font-medium py-2 px-4 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Mail className="w-5 h-5" />
                  Send Reset Link
                </>
              )}
            </button>
            <button
              type="button"
              onClick={() => {
                setShowForgotPassword(false);
                setError(null);
                setSuccess(null);
              }}
              className="mt-2 w-full text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200"
            >
              Back to login
            </button>
          </div>
        )}
        {!isLogin && (
          <div className="mb-4">
            <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Confirm Password
            </label>
            <input
              id="confirmPassword"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              required
            />
          </div>
        )}
        {error && (
          <div className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 border border-red-400 dark:border-red-700 rounded text-sm">
            <p className="text-red-700 dark:text-red-300 mb-2">
              {typeof error === 'object' ? error.message : error}
            </p>
            {error && typeof error === 'object' && error.showSignUpLink && (
              <button
                type="button"
                onClick={() => {
                  setIsLogin(false);
                  setError(null);
                  setSuccess(null);
                }}
                className="text-red-700 dark:text-red-300 hover:underline font-medium"
              >
                Don't have an account? Sign up here →
              </button>
            )}
            {error && typeof error === 'object' && error.showForgotPassword && !showForgotPassword && (
              <button
                type="button"
                onClick={() => {
                  setIsLogin(true); // Switch to login form
                  setShowForgotPassword(true);
                  setError(null);
                  setSuccess(null);
                }}
                className="text-red-700 dark:text-red-300 hover:underline font-medium block mt-2"
              >
                Forgot your password? Reset it here →
              </button>
            )}
          </div>
        )}
        {success && (
          <div className="mb-4 p-3 bg-green-100 dark:bg-green-900/30 border border-green-400 dark:border-green-700 rounded text-sm flex items-start gap-2">
            <CheckCircle2 className="w-5 h-5 text-green-700 dark:text-green-300 mt-0.5 flex-shrink-0" />
            <p className="text-green-700 dark:text-green-300">{success}</p>
          </div>
        )}
        {!showForgotPassword && (
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-primary hover:bg-primary/90 text-white font-medium py-2 px-4 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                {isLogin ? 'Logging in...' : 'Signing up...'}
              </>
            ) : (
              isLogin ? 'Login' : 'Sign Up'
            )}
          </button>
        )}
      </form>

      {!showForgotPassword && (
        <>
          {/* Divider */}
          <div className="relative my-8 md:my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300 dark:border-gray-600"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white dark:bg-slate-950 text-gray-500 dark:text-gray-400">Or</span>
            </div>
          </div>

          {/* Apple Sign In Button */}
          <button
            onClick={handleAppleSignIn}
            disabled={loading}
            className="w-full flex items-center justify-center gap-3 px-4 py-3 border-2 border-gray-800 dark:border-gray-400 rounded-md bg-black dark:bg-gray-100 text-white dark:text-gray-900 hover:bg-gray-900 dark:hover:bg-gray-200 shadow-sm hover:shadow-md transition-all disabled:opacity-50 disabled:cursor-not-allowed font-medium"
          >
            {loading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <>
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
                </svg>
                <span className="font-medium">Continue with Apple</span>
              </>
            )}
          </button>

          {/* Google Sign In Button */}
          <button
            onClick={handleGoogleSignIn}
            disabled={loading}
            className="w-full flex items-center justify-center gap-3 px-4 py-3 border-2 border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 shadow-sm hover:shadow-md transition-all disabled:opacity-50 disabled:cursor-not-allowed font-medium mt-3"
          >
            {loading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <>
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path
                    fill="#4285F4"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="#34A853"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="#FBBC05"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  />
                  <path
                    fill="#EA4335"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  />
                </svg>
                <span className="font-medium">Continue with Google</span>
              </>
            )}
          </button>

          <button
            onClick={() => {
              setIsLogin(!isLogin);
              setError(null);
              setSuccess(null);
            }}
            className="block w-full text-center mt-6 mb-8 md:mb-4 text-sm text-primary hover:underline"
          >
            {isLogin ? "Don't have an account? Sign Up" : 'Already have an account? Login'}
          </button>
        </>
      )}
        </div>
      </div>
        </div>
        <Footer variant="ghost" className="mt-auto" />
      </div>
    </>
  );
}

// Add logout function
export const handleLogout = async () => {
  try {
    await signOut(auth);
    useNavigate()('/login');
  } catch (error) {
    console.error('Error logging out:', error);
  }
};

export default Auth;
